import useSWR from 'swr'
import api from '../util/api'
import {
    Banner,
    BannerSet,
    BannerSlot,
    EMPTY_BANNER_SET,
} from '../component/banner/banner_types'

/*
 * Banner data layer — GET /api/site/banners/
 *
 * Reused verbatim from the shared implementation (banner_types.ts documents the
 * contract): same endpoint, same slot names, same defensive read, same SWR
 * options. Nothing here reshapes, renames, sorts or filters what the API sends
 * beyond dropping entries that carry no usable `image_url`.
 *
 * Relative path only — it rides the same `/api` proxy every other Richard Mille
 * call uses (setupProxy.js in dev, the deployed origin in production), so no
 * host is ever hardcoded.
 *
 * SWR keys on the endpoint string, so every consumer of this hook shares ONE
 * request and one cache entry — mounting it in more than one place does not
 * fetch twice.
 */
export const BANNERS_ENDPOINT = '/api/site/banners/'

/**
 * Defensive read of one slot. The property names and semantics are the API's;
 * this only makes sure a malformed payload can never throw inside render.
 */
function readSlot(raw: any, key: BannerSlot): Banner[] {
    const list = raw?.[key]
    if (!Array.isArray(list)) return []
    // NOTE: no sorting, no filtering — API order is preserved exactly.
    return list.filter(
        (item): item is Banner =>
            !!item && typeof item === 'object' && typeof item.image_url === 'string' && item.image_url.length > 0,
    )
}

async function fetcher(url: string): Promise<BannerSet> {
    const res = await api(url)
    const raw = await res.json()
    return {
        main: readSlot(raw, 'main'),
        left: readSlot(raw, 'left'),
        right: readSlot(raw, 'right'),
        sticky: raw?.sticky === true,
    }
}

export interface UseBannersResult extends BannerSet {
    isLoading: boolean
    /** True when the request failed. The page renders without banners. */
    isError: boolean
}

/**
 * @param enabled  false suspends the request entirely (SWR's null-key idiom).
 *                 Used so the routes and viewports that render no rail — the
 *                 lobby, the game window, every desktop below the rail
 *                 breakpoint, every phone — never ask for banners they cannot
 *                 show, while the component tree above stays mounted and
 *                 unchanged.
 */
export default function useBanners(enabled: boolean = true): UseBannersResult {
    const { data, error, isLoading } = useSWR<BannerSet>(enabled ? BANNERS_ENDPOINT : null, fetcher, {
        revalidateOnFocus: false,
        revalidateIfStale: false,
        shouldRetryOnError: false,
    })

    // A failed or in-flight request is an empty set, never a stand-in: the rails
    // simply do not render. No local fixture ever substitutes for API data.
    const banners: BannerSet = data ?? EMPTY_BANNER_SET

    return {
        ...banners,
        isLoading,
        isError: !!error,
    }
}
